import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, NgZone, OnDestroy, ViewChild, inject } from '@angular/core'

@Component({ selector: 'app-octa-loader', changeDetection: ChangeDetectionStrategy.OnPush, templateUrl: './octa-loader.component.html', styleUrls: ['./octa-loader.component.scss'] })
export class OctaLoaderComponent implements AfterViewInit, OnDestroy {
  // inject ->
  private zone: NgZone = inject(NgZone)

  // variables ->

  @ViewChild('svg', { static: true }) svgRef!: ElementRef<SVGSVGElement>
  @ViewChild('group', { static: true }) groupRef!: ElementRef<SVGGElement>

  private rafId: number = 0

  private circles: SVGCircleElement[] = []

  readonly uid: string = Math.random().toString(36).slice(2, 9)

  @Input() size: number = 200
  @Input() color: string = '#5400BD'
  @Input() speed: number = 2_400
  @Input() ariaLabel: string = 'Loading'

  private static readonly RAW = [
    { sx: 38.84, sy: 44.24, sr: 10.31 },
    { sx: 15.28, sy: 44.16, sr: 8.54 },
    { sx: 6.62, sy: 26.38, sr: 7.05 },
    { sx: 15.29, sy: 9.71, sr: 6.15 },
    { sx: 29.65, sy: 4.65, sr: 5.14 },
    { sx: 42.0, sy: 13.88, sr: 4.62 },
    { sx: 37.92, sy: 24.8, sr: 3.57 },
    { sx: 27.75, sy: 24.33, sr: 1.95 }
  ]

  // state ->
  public ngAfterViewInit(): void {
    this.buildDots()
    this.zone.runOutsideAngular(() => this.start())
  }

  public ngOnDestroy(): void {
    cancelAnimationFrame(this.rafId)
  }

  // utils ->
  private buildDots(): void {
    const SCALE = 2.8
    const SRC_CX = 25
    const SRC_CY = 28
    const STAGE = 200

    const dots = OctaLoaderComponent.RAW.map((d) => ({ x: (d.sx - SRC_CX) * SCALE + STAGE / 2, y: (d.sy - SRC_CY) * SCALE + STAGE / 2, r: d.sr * SCALE * 0.95 })).sort((a, b) => a.r - b.r)

    const NS = 'http://www.w3.org/2000/svg'
    const group = this.groupRef.nativeElement

    this.circles = dots.map((d) => {
      const c = document.createElementNS(NS, 'circle') as SVGCircleElement
      c.setAttribute('fill', this.color)
      c.setAttribute('cx', d.x.toFixed(2))
      c.setAttribute('cy', d.y.toFixed(2))
      c.setAttribute('r', '0')
      ;(c as any).__r = d.r
      group.appendChild(c)
      return c
    })
  }

  private start(): void {
    const N = this.circles.length
    const STAGGER = 180
    const POP = 380
    const HOLD = 650
    const FADE = 260
    const TOTAL = STAGGER * N + POP + HOLD + FADE
    const scale = this.speed / 2_400

    const easeOutBack = (t: number) => {
      const c1 = 1.70158,
        c3 = c1 + 1
      return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2)
    }
    const easeInQuad = (t: number) => t * t

    const startTime = performance.now()
    const tick = (now: number) => {
      const elapsed = ((now - startTime) / scale) % TOTAL
      const popEnd = STAGGER * N + POP
      const holdEnd = popEnd + HOLD

      for (let i = 0; i < N; i++) {
        const c = this.circles[i]
        const baseR = (c as any).__r as number
        let s: number

        if (elapsed < popEnd) {
          const local = (elapsed - i * STAGGER) / POP
          s = local <= 0 ? 0 : local >= 1 ? 1 : Math.max(0, easeOutBack(local))
        } else if (elapsed < holdEnd) {
          s = 1
        } else {
          const f = (elapsed - holdEnd) / FADE
          s = 1 - easeInQuad(Math.min(1, f))
        }

        c.setAttribute('r', (baseR * s).toFixed(2))
      }

      this.rafId = requestAnimationFrame(tick)
    }
    this.rafId = requestAnimationFrame(tick)
  }
}
